import React, { CSSProperties, useContext, useState } from 'react';
import DeleteLibrariesDialog from '../../../components/dialogs/DeleteLibrariesDialog/DeleteLibrariesDialog';
import NewLibraryDialog from '../../../components/dialogs/NewLibraryDialog/NewLibraryDialog';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { LibrarySortInput, User, UserRoleEnum, useGetLibrariesQuery } from '../../../generated/graphql';
import { ClientContext } from '../Clients.Provider';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import { Plus, Tags, Trash2 } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import AddLibrariesTagsDialog from '../../../components/dialogs/AddLibrariesTagsDialog/AddLibrariesTagsDialog';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type LibrariesProps = {
    style?: CSSProperties | undefined;
};

const Libraries = (props: LibrariesProps) => {
    const client = useContext(ClientContext);
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [newDialogVisible, setNewDialogVisible] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [assignTagsDialogVisible, setAssignTagsDialogVisible] = useState(false);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });
    const result = useGetLibrariesQuery({
        variables: {
            contains: searchText,
            clientId: client?.data?.Client?.id,
            order: createGraphQLOrder<LibrarySortInput>(sort),
        }
    });

    const newDialogClose = () => {
        setNewDialogVisible(false);
        result.refetch();
    };
    const deleteDialogClose = () => {
        setDeleteDialogVisible(false);
        result.refetch();
    };
    const assignTagsDialogClose = () => {
        setAssignTagsDialogVisible(false);
        result.refetch();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => result.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={client?.data?.Client?.isAdmin || false}>
                    <button className="btn btn-ghost text-sm" onClick={() => setNewDialogVisible(true)}>
                        <Plus size={15} /> New Library
                    </button>
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" onClick={() => setAssignTagsDialogVisible(true)}>
                                <Tags size={15} /> Tags
                            </button>
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteDialogVisible(true)}>
                                <Trash2 size={15} /> Delete
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>

            <div className="flex-1 min-h-0 relative">
                <div className="absolute inset-0">
                <DataTableNew
                    isLoading={false}
                    data={result.data?.LibraryList?.nodes}
                    link="../Library"
                    showDescription
                    showTags
                    columns={[
                        {
                            id: 'admins',
                            name: 'Library Admins',
                            width: '220px',
                            render: (row) => (
                                <div className="flex flex-wrap gap-1">
                                    {row.admins?.filter((a: User | null) => a).map((a: User) => (
                                        <span key={a.id} className="badge-accent">
                                            {a.name}
                                        </span>
                                    ))}
                                </div>
                            ),
                        },
                    ]}
                    onSearchChanged={(value) => setSearchText(value)}
                    onSelected={(ids) => setSelectedIds(ids)}
                    sortingMode="server"
                    sort={sort}
                    onSortChange={setSort}
                    fetchMore={() => result.data?.LibraryList?.pageInfo?.hasNextPage && result.fetchMore({
                        variables: {
                            contains: searchText,
                            clientId: client?.data?.Client?.id,
                            order: createGraphQLOrder<LibrarySortInput>(sort),
                            after: result.data?.LibraryList?.pageInfo.endCursor,
                        },
                        updateQuery: (previous, { fetchMoreResult }) => ({
                            ...previous,
                            LibraryList: fetchMoreResult?.LibraryList && previous.LibraryList ? {
                                ...fetchMoreResult.LibraryList,
                                nodes: [...(previous.LibraryList.nodes ?? []), ...(fetchMoreResult.LibraryList.nodes ?? [])],
                            } : previous.LibraryList,
                        }),
                    })}
                />
                </div>
            </div>

            <NewLibraryDialog clientId={client?.data?.Client?.id} visible={newDialogVisible} onClose={newDialogClose} />
            <DeleteLibrariesDialog clientId={client?.data?.Client?.id || ""} ids={selectedIds} visible={deleteDialogVisible} onClose={deleteDialogClose} />
            {assignTagsDialogVisible && <AddLibrariesTagsDialog ids={selectedIds} visible={assignTagsDialogVisible} onClose={assignTagsDialogClose} />}
        </div>
    );
};

export default Libraries;
